import { useEffect, useState } from "react";
import { Video, Loader2 } from "lucide-react";
import { SPONSOR_SECONDS } from "@/lib/h5ads";

/**
 * AdPlayingOverlay — in-page chrome that renders WHILE the H5 adBreak()
 * request is in-flight. Google's rewarded video iframe draws INSIDE this
 * backdrop, so the player sees a controlled "AD PLAYING" surface instead
 * of thinking a new tab was blocked. Pure CSS backdrop + spinner — the
 * actual ad frame is injected by the AdSense SDK on top.
 *
 * Call sites open this immediately on the click handler (sync with the
 * user gesture) so browsers don't flag it as an interstitial popup.
 */
export default function AdPlayingOverlay({ open, label = "AD PLAYING" }) {
  const [left, setLeft] = useState(SPONSOR_SECONDS);
  useEffect(() => {
    if (!open) return;
    setLeft(SPONSOR_SECONDS);
    const id = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [open]);
  // Lock scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/95"
      data-testid="ad-playing-overlay"
      style={{ backdropFilter: "blur(6px)" }}
    >
      {/* Top strip — thin gold line + label */}
      <div
        className="absolute top-0 left-0 right-0 h-10 flex items-center justify-center text-[10px] tracking-[0.5em] gold-text"
        style={{ borderBottom: "1px solid rgba(212,175,55,0.4)", background: "rgba(10,5,2,0.95)" }}
      >
        <Video size={14} className="mr-2" />
        {label}
      </div>

      {/* Center spinner — Google's rewarded iframe appears over this */}
      <div className="text-center">
        <Loader2 size={32} className="animate-spin gold-text mx-auto" />
        <div className="font-heading text-2xl gold-text mt-4" data-testid="ad-countdown">
          {left > 0 ? `REWARD IN ${left}s` : "REWARD COMING..."}
        </div>
      </div>

      {/* Bottom strip — reassurance */}
      <div
        className="absolute bottom-0 left-0 right-0 py-2 text-center text-[10px] tracking-[0.3em] ash-text"
        style={{ borderTop: "1px solid rgba(212,175,55,0.25)" }}
      >
        REWARD GRANTED AUTOMATICALLY WHEN AD FINISHES
      </div>
    </div>
  );
}
